import { useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Wallet, Image as ImageIcon, X } from "lucide-react";
import { AuthImage } from "@/components/AuthImage";
import { AuthImageLightbox } from "@/components/AuthImageLightbox";

const STATUS_STYLES = {
  PENDING: "bg-amber-100 text-amber-700 border-amber-200",
  VALIDATED: "bg-emerald-100 text-emerald-700 border-emerald-200",
  REJECTED: "bg-red-100 text-red-700 border-red-200",
};

const MODE_STYLES = {
  CASH: "bg-emerald-100 text-emerald-700 border-emerald-200",
  CHEQUE: "bg-blue-100 text-blue-700 border-blue-200",
  NEFT: "bg-purple-100 text-purple-700 border-purple-200",
  UPI: "bg-amber-100 text-amber-700 border-amber-200",
};

const formatCurrency = (paise) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format((paise || 0) / 100);

function fmt(ts) {
  if (!ts) return "—";
  try { return new Date(ts).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }); } catch { return ts; }
}

/**
 * <CollectionDetailDialog>
 * Read-only viewer for a collection (cheque/cash/UPI payment record) with
 * thumbnail strip of attached photos. Click a thumbnail to open the lightbox.
 */
export function CollectionDetailDialog({ open, onClose, collection, party, employee }) {
  const [lightboxPath, setLightboxPath] = useState(null);
  if (!collection) return null;

  const photos = collection.photos || [];

  return (
    <>
      <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
        <DialogContent className="max-w-2xl" data-testid="collection-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wallet className="w-5 h-5 text-[#8B1A1A]" />
              Collection {collection.collection_id}
              <Badge variant="outline" className={`ml-2 text-xs ${STATUS_STYLES[collection.status] || ""}`}>
                {collection.status}
              </Badge>
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Summary card */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 p-4 rounded-lg bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700">
              <div>
                <p className="text-[10px] uppercase tracking-wider text-slate-500">Amount</p>
                <p className="text-lg font-bold text-emerald-600 font-mono mt-0.5">{formatCurrency(collection.amount)}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-slate-500">Mode</p>
                <Badge variant="outline" className={`mt-0.5 ${MODE_STYLES[collection.mode] || ""}`}>{collection.mode}</Badge>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-slate-500">Date</p>
                <p className="text-sm font-medium mt-0.5">{collection.date || "—"}</p>
              </div>
              <div className="col-span-2">
                <p className="text-[10px] uppercase tracking-wider text-slate-500">Dealer</p>
                <p className="text-sm font-medium mt-0.5">{party?.firm_name || collection.party_id || "—"}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-slate-500">Collected by</p>
                <p className="text-sm font-medium mt-0.5">{employee?.full_name || collection.employee_id || "—"}</p>
              </div>
              {collection.reference_no && (
                <div className="col-span-2 sm:col-span-3">
                  <p className="text-[10px] uppercase tracking-wider text-slate-500">Reference</p>
                  <p className="text-sm font-mono mt-0.5">{collection.reference_no}</p>
                </div>
              )}
              {collection.mode === "CHEQUE" && (
                <>
                  {collection.cheque_number && <div><p className="text-[10px] uppercase tracking-wider text-slate-500">Cheque #</p><p className="text-sm font-mono mt-0.5">{collection.cheque_number}</p></div>}
                  {collection.cheque_bank && <div><p className="text-[10px] uppercase tracking-wider text-slate-500">Bank</p><p className="text-sm mt-0.5">{collection.cheque_bank}</p></div>}
                  {collection.cheque_date && <div><p className="text-[10px] uppercase tracking-wider text-slate-500">Cheque date</p><p className="text-sm mt-0.5">{collection.cheque_date}</p></div>}
                </>
              )}
            </div>

            {/* Photo thumbnails */}
            <div>
              <div className="flex items-center gap-1.5 mb-2">
                <ImageIcon className="w-3.5 h-3.5 text-slate-400" />
                <p className="text-xs font-semibold text-slate-600 dark:text-slate-300">
                  Attached photos <span className="text-slate-400 font-normal">({photos.length})</span>
                </p>
              </div>
              {photos.length === 0 ? (
                <p className="text-xs text-slate-400 italic">No photos attached.</p>
              ) : (
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="collection-photo-grid">
                  {photos.map((p, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setLightboxPath(p)}
                      className="aspect-square rounded-md overflow-hidden border border-slate-200 dark:border-slate-700 hover:border-[#8B1A1A] transition focus:outline-none focus:ring-2 focus:ring-[#8B1A1A]"
                      data-testid={`collection-photo-${i}`}
                    >
                      <AuthImage path={p} alt={`Photo ${i + 1}`} className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Validation/Rejection footer */}
            {(collection.status === "VALIDATED" || collection.status === "REJECTED") && (
              <div className="text-xs text-slate-500 border-t pt-2 dark:border-slate-700">
                {collection.status === "VALIDATED" ? (
                  <>Validated at <span className="font-medium">{fmt(collection.validated_at)}</span>{collection.validated_by ? <> by <span className="font-medium">{collection.validated_by}</span></> : null}</>
                ) : (
                  <>Rejected{collection.rejection_reason ? <> — <span className="italic">{collection.rejection_reason}</span></> : null}</>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="secondary" onClick={onClose} className="bg-white border-2 border-slate-300 text-slate-700">
              <X className="w-4 h-4 mr-1" /> Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {lightboxPath && (
        <AuthImageLightbox path={lightboxPath} onClose={() => setLightboxPath(null)} />
      )}
    </>
  );
}

export default CollectionDetailDialog;
